import { useState } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { EASE_EXPO } from "@/lib/motion";
import { WhatsAppButton } from "./WhatsAppButton";
import { TextReveal } from "@/components/ui/TextReveal";

const faqs = [
  {
    q: "Where do you deliver?",
    a: "We do local pickup from our kitchen in Pune, delivery within Pune and Karad (Charges may apply). For nearby areas, message us — we'll try our best to work something out.",
  },
  {
    q: "Do you offer 100% Pure Vegetarian, Whole Wheat, or Jaggery/Less Sugar cakes for kids?",
    a: "Yes! All our cakes, cookies, and recipes are 100% Pure Vegetarian. We also offer child-friendly recipes made with pure organic jaggery, whole wheat flour, and zero refined sugars or artificial preservatives.",
  },
  {
    q: "How much notice do you need?",
    a: "Most bakes need about 24 hours' notice. Bulk orders and large tins are best placed 2–3 days in advance. For festive periods, please plan a week ahead.",
  },
  {
    q: "How long do your bakes stay fresh?",
    a: "Cakes and Cup cakes stay for 3 days. Cookies stay fresh for 8-10 days in a air tight container depending on type and climate. Premixes have a shelf-life mentioned and for others will be informed.",
  },
  {
    q: "Do you take bulk and corporate orders?",
    a: "Yes, we happily bake for gifting boxes, hampers and celebrations. Send us the quantity and timeline on WhatsApp — we'll confirm feasibility and share pricing.",
  },
  {
    q: "How do I place an order?",
    a: "Message us on WhatsApp with what you want, when you want it, and any special requests. We'll confirm timing, pricing and pickup/delivery.",
  },
];

const Item = ({ f, i, open, setOpen }) => {
  const isOpen = open === i;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.38, delay: i * 0.04, ease: EASE_EXPO }}
      className="border-b border-[#3B2818]/15"
    >
      <button
        data-testid={`faq-toggle-${i}`}
        onClick={() => setOpen(isOpen ? -1 : i)}
        aria-expanded={isOpen}
        className="w-full py-4 sm:py-5 md:py-6 flex items-center justify-between gap-4 text-left group transition-transform duration-150 ease-out active:scale-[0.995]"
      >
        <span className="font-serif text-lg sm:text-xl md:text-2xl lg:text-3xl leading-snug text-[#3B2818] group-hover:text-[#C06A22] transition-colors duration-200">
          {f.q}
        </span>
        <span
          className={`shrink-0 h-8 w-8 md:h-10 md:w-10 rounded-full border border-[#3B2818]/25 flex items-center justify-center transition-[transform,background-color,color,border-color] duration-250 ease-spring-back ${
            isOpen ? "rotate-45 bg-[#3B2818] text-[#FAF6F0] border-[#3B2818]" : "text-[#3B2818]"
          }`}
        >
          <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={1.8} />
        </span>
      </button>
      <div
        className="grid-accordion transition-[grid-template-rows] duration-250 ease-expo-fast"
        data-expanded={isOpen}
      >
        <div className="overflow-hidden">
          <p
            data-testid={`faq-answer-${i}`}
            className="pb-4 sm:pb-5 pr-8 md:pr-14 text-xs sm:text-sm md:text-[15px] leading-relaxed text-[#6B5343] max-w-3xl"
          >
            {f.a}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-12 sm:py-16 md:py-20"
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-12 gap-6 lg:gap-8">
          <div className="col-span-12 lg:col-span-4">
            <span className="text-[11px] uppercase tracking-[0.28em] text-[#C06A22]">
              Good to know
            </span>
            <TextReveal className="font-serif text-[28px] sm:text-[36px] md:text-5xl leading-[1.05] tracking-tight mt-2 text-[#3B2818]">
              Small questions, warm answers.
            </TextReveal>
            <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-[#6B5343] max-w-xs">
              Anything else? Send it over on WhatsApp — usually a reply within
              the hour.
            </p>
            <div className="mt-5 sm:mt-6">
              <WhatsAppButton
                variant="ghost"
                size="md"
                label="Ask us anything"
                testId="faq-wa-cta"
                msg="Hi! I have a quick question about your bakes."
              />
            </div>
          </div>

          <div className="col-span-12 lg:col-span-8">
            {faqs.map((f, i) => (
              <Item
                key={f.q}
                f={f}
                i={i}
                open={open}
                setOpen={setOpen}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
